// Centralized logout handler for immediate cross-tab synchronization
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Create a single BroadcastChannel instance for the entire app
let logoutChannel = null;

const getLogoutChannel = () => {
  if (!logoutChannel && typeof BroadcastChannel !== 'undefined') {
    logoutChannel = new BroadcastChannel('logout_sync');
  }
  return logoutChannel;
};

// Role to login route mapping
const roleRedirectMap = {
  TPRM: '/tprm_login',
  TPRMUSER: '/tprm_user_login',
  VRM: '/vrm_login',
  OFFSEC_STAFF: '/offsec_staff_login',
  AUDIT: '/audit_login',
  CARA: '/cara_login',
  CARAA: '/login',
  SUPERADMIN: '/superadmin_login',
  INTERNALSTAFF: '/internal_staff_login',       //  CARAA admin users login from /login page
  ADMIN: '/login',       //  ADMIN users login from /login page
  DEFAULT: '/login',
  '1': '/login',         // Admin user_type
  '2': '/cara_login',    // CARA user_type
  '14': '/tprm_login',   // TPRM user_type
  '15': '/vrm_login',    // VRM user_type
  '16': '/cara_login',   // Auditor user_type
  '19': '/tprm_user_login' // TPRM User user_type
};

const getLoginRoute = (userType, role) => {
  // Check user_type first
  if (userType && roleRedirectMap[userType]) {
    return roleRedirectMap[userType];
  }
  // Check role
  if (role && roleRedirectMap[role]) {
    return roleRedirectMap[role];
  }
  // Default
  return roleRedirectMap.DEFAULT;
};

/**
 * Clear all session data from storage
 */
export const clearAllSessionData = () => {
  const keysToRemove = [
    'industry',
    'user_id',
    'username',
    'role',
    'user',
    'userRole',
    'lastActivity',
    'caraUser',
    'tprmUser',
    'caraAdmin',
    'tprmUseruser',
    'email',
    'first_name',
    'last_name',
    'loginType',
    'logout_event',
    'logout_immediate',
    //  Set on login by one or more login pages (CaraAdmin/CaraLogin/TPRM/TPRMUser)
    // but previously never cleared here, leaving stale data behind after logout.
    'session_id',
    'otp_bypass_mode',
    'authCache',
    'device_type',
    'browser',
    // Stale cara_timezone left behind after logout was being read back in by
    // the next account to log in on the same browser (e.g. OffSec staff
    // after a regular CARA session), showing times in the wrong zone.
    'cara_timezone',
  ];

  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

/**
 * Immediate logout handler that synchronizes across all tabs
 * @param {string} userType - The user type for determining redirect route
 * @param {string} role - The user role for determining redirect route
 * @param {boolean} skipConfirm - Skip confirmation dialog
 * @returns {Promise<void>}
 */
export const performLogout = async (userType = null, role = null, skipConfirm = false) => {
  // Prevent multiple simultaneous logout calls
  if (window.__LOGOUT_IN_PROGRESS__) {
    console.log(' Logout already in progress, skipping...');
    return;
  }

  window.__LOGOUT_IN_PROGRESS__ = true;

  try {
    //  CRITICAL: Get username FIRST, before ANY events or broadcasts
    const username = localStorage.getItem('username');

    // Call logout API immediately with captured username
    console.log('📞 Calling logout API...');
    if (username) {
      try {
        const response = await axios.post(`${API_URL}api/logout/`, { username }, {
          withCredentials: true,
          timeout: 3000 // Increased timeout to ensure completion
        });
      } catch (err) {
        console.error(" Logout API call failed:", err.message);
        console.error("Full error:", err);
      }
    } else {
      console.warn(' No username found in localStorage for logout API call');
    }

    // IMMEDIATE: Broadcast logout to all tabs
    const channel = getLogoutChannel();
    if (channel) {
      console.log('📡 Broadcasting logout to all tabs...');
      channel.postMessage({
        type: 'IMMEDIATE_LOGOUT',
        timestamp: Date.now(),
        userType,
        role
      });
    }

    // IMMEDIATE: Set localStorage flag for browsers without BroadcastChannel
    localStorage.setItem('logout_immediate', Date.now().toString());

    // IMMEDIATE: Dispatch custom event
    window.dispatchEvent(new CustomEvent('userLogout', {
      detail: { userType, role, immediate: true }
    }));

    console.log('🧹 Clearing all session data...');
    // Clear all session data immediately
    clearAllSessionData();

    // Determined redirect route
    const loginRoute = getLoginRoute(userType, role);
    console.log(` Redirecting to: ${loginRoute}`);

    // Give a small delay for toast messages and UI updates to be visible
    setTimeout(() => {
      window.location.href = loginRoute;
    }, 1000);

  } catch (error) {
    console.error('Logout error:', error);
    // Force redirect even on error
    window.location.href = '/login';
  } finally {
    // Clean up flag after a short delay
    setTimeout(() => {
      window.__LOGOUT_IN_PROGRESS__ = false;
    }, 1000);
  }
};

/**
 * Setup global listeners for logout events from other tabs
 * Call this once in your App.js or main entry point
 */
export const setupGlobalLogoutListeners = () => {
  // Listen to BroadcastChannel
  const channel = getLogoutChannel();
  if (channel) {
    channel.onmessage = (event) => {
      if (event.data.type === 'IMMEDIATE_LOGOUT') {
        console.log('🚨 Received immediate logout signal from another tab');
        clearAllSessionData();
        const loginRoute = getLoginRoute(event.data.userType, event.data.role);
        window.location.href = loginRoute;
      }
    };
  }

  // Listen to localStorage changes (fallback for older browsers)
  window.addEventListener('storage', (event) => {
    if (event.key === 'logout_immediate' && event.newValue) {
      console.log('🚨 Received immediate logout signal via localStorage');
      // Small delay to ensure the original tab has cleaned up
      setTimeout(() => {
        clearAllSessionData();
        const role = localStorage.getItem('role');
        const loginRoute = getLoginRoute(null, role);
        window.location.href = loginRoute;
      }, 50);
    }
  });

  // Listen to custom event (for same-tab logout)
  window.addEventListener('userLogout', (event) => {
    if (event.detail?.immediate) {
      console.log('🚨 Received immediate logout event');
      clearAllSessionData();
      const loginRoute = getLoginRoute(event.detail.userType, event.detail.role);
      window.location.href = loginRoute;
    }
  });

  console.log(' Global logout listeners initialized');
};

/**
 * Cleanup function
 */
export const cleanupLogoutListeners = () => {
  if (logoutChannel) {
    logoutChannel.close();
    logoutChannel = null;
  }
};

